/**
 * This file acts as a client-side API client for our Supabase Edge Functions
 * It provides a clean interface for the frontend to interact with Slack
 */
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// API Base URL - either use the deployed URL or local dev URL
const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 
  'https://gdpicetapcjfiosrgoky.supabase.co/functions/v1';

/**
 * Get the authentication token from Supabase auth
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();

  // No active session
  if (!session) return null;

  // If token is expired, try to refresh
  if (session.expires_at && session.expires_at * 1000 < Date.now()) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Failed to refresh Supabase session:', error);
      return null;
    }
    return refreshed.session?.access_token || null;
  }

  return session.access_token;
}

/**
 * Generic function to make authenticated requests to Supabase Edge Functions
 */
async function callEdgeFunction(path: string, method: string = 'GET', body?: any): Promise<any> {
  let token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const options: RequestInit = {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  
  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    options.body = JSON.stringify(body);
  }
  
  console.log(`Calling edge function: ${SUPABASE_FUNCTIONS_URL}/${path}`);
  
  const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/${path}`, options);
  
  // If token invalid, attempt to refresh once and retry
  if (response.status === 401) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (!error && refreshed.session) {
      token = refreshed.session.access_token;
      // Create a new headers object to avoid TypeScript issues with HeadersInit
      options.headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      const retryRes = await fetch(`${SUPABASE_FUNCTIONS_URL}/${path}`, options);
      if (!retryRes.ok) {
        const errText = await retryRes.text();
        throw new Error(`API error: ${retryRes.status} - ${errText}`);
      }
      return retryRes.json();
    }
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }
  
  return response.json();
}

/**
 * Slack API functions
 */
export const slackApi = {
  /**
   * Get Slack workspace info for the current organization
   */
  getWorkspaceInfo: async () => {
    try {
      const data = await callEdgeFunction('slack-api/workspace-info');
      return {
        connected: !!data.connected,
        workspace: data.workspace || null,
        message: data.message || null,
        error: data.error || null
      };
    } catch (error: any) {
      console.error('Error getting workspace info:', error);
      return {
        connected: false,
        workspace: null,
        message: 'Failed to connect to Slack API',
        error: error.message || 'Unknown error'
      };
    }
  },
  
  /**
   * List all Slack users in the connected workspace
   */
  listUsers: async () => {
    try {
      const data = await callEdgeFunction('slack-api/users');
      return {
        users: data.users || [],
        message: data.message || null,
        error: data.error || null
      };
    } catch (error: any) {
      console.error('Error listing Slack users:', error);
      return {
        users: [],
        message: 'Failed to fetch Slack users',
        error: error.message || 'Unknown error'
      };
    }
  },
  
  /**
   * List all Slack channels in the connected workspace
   */
  listChannels: async () => {
    try {
      const data = await callEdgeFunction('slack-api/channels');
      return {
        channels: data.channels || [],
        message: data.message || null,
        error: data.error || null
      };
    } catch (error: any) {
      console.error('Error listing Slack channels:', error);
      return {
        channels: [],
        message: 'Failed to fetch Slack channels',
        error: error.message || 'Unknown error'
      };
    }
  },
  
  /**
   * Send a message to a Slack channel or user
   */
  sendMessage: async (params: {
    channel?: string;
    user_id?: string;
    text?: string;
    blocks?: any[];
  }) => {
    try {
      const data = await callEdgeFunction('slack-api/send-message', 'POST', params);
      return {
        success: true,
        data,
        error: null
      };
    } catch (error: any) {
      console.error('Error sending Slack message:', error);
      return {
        success: false,
        data: null,
        error: error.message || 'Unknown error'
      };
    }
  },
  
  /**
   * Start the Slack OAuth flow
   * This will redirect the user to Slack's authorization page
   */
  initiateOAuth: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !session.user) {
      toast.error("You must be logged in to connect to Slack");
      return;
    }
    
    // Encode user ID in the state parameter
    const state = btoa(JSON.stringify({ userId: session.user.id }));
    
    // Redirect to the Slack OAuth endpoint with the state parameter
    window.location.href = `${SUPABASE_FUNCTIONS_URL}/slack-oauth?state=${state}`;
  },
};

export default slackApi; 